import type { InternalRequest } from '../client.js';

type Params = Record<string, string | number | boolean | undefined | null>;

function qsFrom(params?: Params): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== '');
  if (!entries.length) return '';
  const usp = new URLSearchParams();
  for (const [k, v] of entries) usp.set(k, String(v));
  return `?${usp}`;
}

/**
 * Best-effort SOS location (spec §5). `lat`/`lng` match the web SOSAlertCard
 * and the server's stored shape (server/src/safety/sos-location.ts). The server
 * stores `fix_time: null` for a missing or unparseable instant and
 * `accuracy_m: null` when unknown.
 */
export interface SosLocationBody {
  lat: number;
  lng: number;
  accuracy_m: number | null;
  fix_time: string | null;
  source: 'last_known' | 'current';
}

export function safetyEndpoints(req: InternalRequest) {
  return {
    /**
     * POST /safety/sos — trigger an SOS alert for the authenticated staff member.
     * Server inserts into sos_alerts and broadcasts to the org room via WebSocket.
     *
     * `client_id` is the dedupe key for mobile retry loops (migration 029):
     * the server's unique index on (organization_id, client_id) returns the
     * original alert on repeat POSTs instead of creating duplicates. A repeat
     * by another member answers 409 `client_id_conflict`.
     *
     * `location` is best-effort (never wait for a fix); declare
     * SOS_LOCATION_CAPABILITY in `capabilities` when sending it.
     */
    triggerSos: (body: {
      firedAt: number;
      lat?: number;
      lon?: number;
      client_id?: string;
      location?: SosLocationBody;
      capabilities?: string[];
    }) =>
      req.request<unknown>('/safety/sos', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /**
     * GET /safety/sos/:id — single alert's acknowledgment status. The mobile
     * SOS retry loop polls this after delivery (a cheap single-row read)
     * instead of re-POSTing /safety/sos every 5s to detect the manager ack.
     */
    getSos: (id: string) => req.request<unknown>(`/safety/sos/${id}`),

    checkins: {
      /**
       * GET /safety/checkins — list past check-in events for the org. Optional
       * `from` / `to` ISO date filters; `mine: true` narrows to the caller's
       * own rows (servers predating the param ignore it — filter client-side
       * by `user_id` too). Returns up to 200 most-recent events.
       */
      list: (params?: { from?: string; to?: string; mine?: boolean }) =>
        req.request<unknown[]>(`/safety/checkins${qsFrom(params)}`),

      /**
       * POST /safety/checkins — staff member self-initiates a check-in event
       * ("I'm OK"). Server inserts a row into checkin_events tagged with the
       * authenticated user. Optional `location` field (lat/lng object) is
       * stored as JSONB. `type` defaults to 'self_checkin' server-side if
       * omitted.
       *
       * Plan 6 v1: scheduled check-ins are NOT yet a server concept; this is
       * the self-initiated "I'm OK" path. v1.x may add scheduling +
       * acknowledge semantics — at which point this method gains a sibling.
       */
      create: (body?: {
        type?: string;
        location?: { lat: number; lng: number };
      }) =>
        req.request<unknown>('/safety/checkins', {
          method: 'POST',
          body: JSON.stringify(body ?? {}),
        }),
    },
  };
}
